import type { IPagedResponse, OrderByParam, PaginationParam } from '@/features/api/extranet'
import { usePreferencesUtilisateur } from '@/features/application/preferences'
import {
  PREFERENCES_UTILISATEUR_APP,
  PREFERENCES_UTILISATEUR_APP_PAGESIZE,
  type PreferenceGlobale,
} from '@/features/application/types.ts'
import { loadRecherche, printRecherche } from '@/features/services/recherche'
import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import type { RouteParamsGeneric, RouteRecordNameGeneric } from 'vue-router'
import { useImmerFieldPatcher } from '@/features/interface/composables'
import { isObjectEquals } from '@/features/shared/tools'
import { navigateTo, type NavigateToQuery } from '@/router/navigateTo'
import { formatSearchQuery, parseSearchQuery } from '@/features/shared/tools/parse.ts'
import { type ShowDialogOptions, useDialog } from '@/features/interface/dialog'

export type QueryBase = {
  search?: string[]
}

export type PagedRequest<T extends QueryBase> = {
  query: T extends QueryBase ? T : never
  orderBy: OrderByParam
  pagination: PaginationParam
  route?: {
    name: RouteRecordNameGeneric
    params: RouteParamsGeneric
  }
}

export type BuilRechercheOptions = {
  storeName: string
  apiPath: string
  location: NavigateToQuery
}

export type BuildRechercheStore<TQuery extends QueryBase, TResponse extends object> =
  TQuery extends QueryBase ? typeof buildRechercheStore<TQuery, TResponse> : never
export type RechercheStoreDefinition<TQuery extends QueryBase, TResponse extends object> = TQuery extends QueryBase
  ? ReturnType<BuildRechercheStore<TQuery, TResponse>>
  : never
export type RechercheStore<TQuery extends QueryBase, TResponse extends object> = TQuery extends QueryBase
  ? ReturnType<RechercheStoreDefinition<TQuery, TResponse>>
  : never

export function buildRechercheStore<TQuery extends QueryBase, TResponse extends object>(
  builderOptions: BuilRechercheOptions,
) {
  return defineStore(builderOptions.storeName, () => {
    const pageData = shallowRef<IPagedResponse<TResponse> | null>(null)
    const pageRequest = shallowRef<PagedRequest<TQuery> | null>(null)
    const isInitRequest = ref<boolean>(true)

    function $reset() {
      pageData.value = null
      pageRequest.value = null
      isInitRequest.value = true
    }

    async function loadData(request: PagedRequest<TQuery>) {
      if (!request.pagination.pageSize) {
        const prefService = usePreferencesUtilisateur<PreferenceGlobale>(PREFERENCES_UTILISATEUR_APP)
        const userPref = await prefService.getPreferencesAsync()
        request.pagination.pageSize = userPref?.pageSize ?? PREFERENCES_UTILISATEUR_APP_PAGESIZE
      }

      const result = await loadRecherche<TQuery, TResponse>(builderOptions.apiPath, request, {
        sablier: isInitRequest.value ? true : 1000,
        autoCancel: !isInitRequest.value,
      })
      if (isInitRequest.value) {
        isInitRequest.value = false
      }

      // Modifie le state si pas d'erreur
      pageRequest.value = request
      pageData.value = result
    }

    async function reloadData() {
      // Modifie le state si pas d'erreur
      pageData.value = await loadRecherche<TQuery, TResponse>(builderOptions.apiPath, pageRequest.value!)
    }

    const { setupFieldWithKey, setupFieldWithOptions, setupCustomUpdater, setupArrayUpdaterWithKey } =
      useImmerFieldPatcher<PagedRequest<TQuery>>({
        getModel: () => pageRequest.value!,
        async setModel(newModel) {
          const queryChanged = !isObjectEquals(pageRequest.value!.query, newModel.query)
          if (queryChanged) {
            newModel = {
              ...newModel,
              pagination: {
                pageNumber: 1,
                pageSize: newModel.pagination.pageSize,
              },
            }
          }
          try {
            await navigateTo({
              ...builderOptions.location,
              ...newModel,
            } as NavigateToQuery)
          } catch (error) {
            if (error instanceof DOMException) return
            throw error
          }
        },
        debounceDelay: 500,
      })

    const order = setupFieldWithOptions<string>({
      getValue: () => {
        return pageRequest.value?.orderBy?.at(0) ?? ''
      },
      setPatch: (patch, value) => {
        patch.orderBy = value ? [value] : []
        patch.pagination = {
          pageNumber: 1,
          pageSize: patch.pagination.pageSize,
        }
      },
    })

    // Todo : Rechercher motif probléme de type
    const search = setupFieldWithOptions<string>({
      getValue: () => formatSearchQuery(pageRequest.value?.query?.search ?? []),
      setPatch: (patch, value) => {
        if (!value) (patch.query as QueryBase).search = []
        else (patch.query as QueryBase).search = parseSearchQuery(value)
      },
      withDebounce: true,
    })

    // Exporter les dossiers
    async function exportData(dialogIngo: Pick<ShowDialogOptions, 'title' | 'message' | 'isHtml'>) {
      await printRecherche<TQuery>(builderOptions.apiPath, pageRequest.value!)
      await useDialog().showDialog({
        type: 'Ok',
        closable: true,
        ...dialogIngo,
      })
    }

    return {
      $reset,
      loadData,
      reloadData,
      exportData,
      pageRequest,
      pageData,
      order,
      search,
      setupFieldWithKey,
      setupFieldWithOptions,
      setupCustomUpdater,
      setupArrayUpdaterWithKey,
    }
  })
}
